import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  Map, 
  Layers, 
  List,
  Beef,
  Clock,
  Leaf
} from 'lucide-react';
import { getPaddocks, getGrazingTracker } from '../../services/api';

// You'll need to replace this with your Mapbox access token
// Get one free at: https://account.mapbox.com/access-tokens/
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'YOUR_MAPBOX_TOKEN_HERE';

// Default center (Uganda - adjust to your ranch location)
const DEFAULT_CENTER = [32.5825, 0.3476]; // Longitude, Latitude
const DEFAULT_ZOOM = 14;

// Status colors for paddocks
const STATUS_COLORS = {
  grazing: '#22c55e',    // Green
  resting: '#f59e0b',    // Amber
  ready: '#3b82f6',      // Blue
};

const STATUS_LABELS = {
  grazing: 'Grazing',
  resting: 'Resting',
  ready: 'Ready to Graze',
};

// Grazing-intelligence Phase 8a — recency ramp (clay = just grazed → green =
// long rested/recovered), by days since grazed. Owner-facing; no SAR/NDVI.
const RECENCY_CLAY = '#b45309';   // grazed very recently
const RECENCY_MID = '#eab308';    // regrowing
const RECENCY_GREEN = '#16a34a';  // rested / recovered
const RECENCY_ATTENTION = '#dc2626'; // looks grazed while resting
const RECENCY_UNKNOWN = '#9ca3af';   // no data

// Resolve a paddock's recency fill from the grazing tracker row. Clay when
// just grazed, warming to green as it rests; red when it looks grazed while
// resting (the attention case); grey when the satellite has no read.
function resolveRecencyColor(t) {
  if (!t || t.utilization_state == null) return RECENCY_UNKNOWN;
  if (t.utilization_state === 'attention') return RECENCY_ATTENTION;
  const d = t.days_since_grazed;
  if (d == null) return RECENCY_GREEN; // never grazed / long rested
  if (d <= 7) return RECENCY_CLAY;
  if (d <= 21) return RECENCY_MID;
  if (d <= 45) return '#84cc16'; // lime — well into recovery
  return RECENCY_GREEN;
}

export default function PaddockMap({ ranchId }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  // Registered once per map instance (layer-scoped handlers survive layer
  // re-adds) and fit-to-bounds only on first build — so re-running the layer
  // effect (tracker load, color-mode toggle) doesn't leak listeners or yank
  // the user's pan/zoom back to the fitted bounds.
  const handlersBound = useRef(false);
  const didFit = useRef(false);
  const [selectedPaddock, setSelectedPaddock] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [colorMode, setColorMode] = useState('status'); // 'status' or 'recency'

  // Fetch paddocks
  const { data: paddocks = [], isLoading } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  });

  // Grazing tracker — recency + rotation path for the 'recency' color mode.
  const { data: tracker } = useQuery({
    queryKey: ['grazingTracker', ranchId],
    queryFn: ({ signal }) => getGrazingTracker(ranchId, { signal }),
    staleTime: 5 * 60_000,
  });
  const trackerByPaddock = {};
  for (const p of tracker?.paddocks || []) trackerByPaddock[p.paddock_id] = p;

  // Initialize map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      // New map instance on remount needs its handlers/fit re-applied.
      handlersBound.current = false;
      didFit.current = false;
    };
  }, []);

  // Add paddock layers when map is loaded and paddocks are fetched
  useEffect(() => {
    if (!map.current || !mapLoaded || !paddocks.length) return;

    // Build GeoJSON polygons from each paddock's real boundary_coordinates.
    // Fall back to a mock rectangle only when boundary data is missing — that
    // makes the gap visible instead of silently hiding the paddock.
    const paddockFeatures = paddocks.map((paddock, index) => {
      const bc = paddock.boundary_coordinates;
      let coordinates;

      if (Array.isArray(bc) && bc.length >= 3) {
        // DB stores [{lat, lng}, ...] — Mapbox wants [lng, lat] and a closed ring.
        const ring = bc
          .filter(p => p && Number.isFinite(+p.lng) && Number.isFinite(+p.lat))
          .map(p => [Number(p.lng), Number(p.lat)]);
        if (ring.length >= 3) {
          const [fx, fy] = ring[0];
          const [lx, ly] = ring[ring.length - 1];
          if (fx !== lx || fy !== ly) ring.push([fx, fy]);
          coordinates = [ring];
        }
      }

      if (!coordinates) {
        // Fallback — paddock has no geometry. Place near default center so
        // it's visible but obviously not real.
        const offsetLng = (index % 3) * 0.005 - 0.005;
        const offsetLat = Math.floor(index / 3) * 0.004 - 0.004;
        const centerLng = DEFAULT_CENTER[0] + offsetLng;
        const centerLat = DEFAULT_CENTER[1] + offsetLat;
        const size = Math.sqrt(paddock.area_ha || 10) * 0.001;
        coordinates = [[
          [centerLng - size, centerLat - size],
          [centerLng + size, centerLat - size],
          [centerLng + size, centerLat + size],
          [centerLng - size, centerLat + size],
          [centerLng - size, centerLat - size],
        ]];
      }

      const t = trackerByPaddock[paddock.id] || {};
      // recency_color is resolved here (JS) rather than in a Mapbox expression
      // so the clay→green ramp + attention/unknown cases stay in one place.
      const recencyColor = resolveRecencyColor(t);

      return {
        type: 'Feature',
        properties: {
          id: paddock.id,
          code: paddock.code,
          name: paddock.name,
          area_ha: paddock.area_ha,
          status: paddock.status?.current_status || 'resting',
          cattle_count: paddock.status?.cattle_count || 0,
          days_rested: paddock.status?.days_rested || 0,
          is_ready: paddock.status?.is_ready_to_graze || false,
          days_since_grazed: t.days_since_grazed ?? null,
          utilization_state: t.utilization_state || null,
          recency_color: recencyColor,
        },
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      };
    });

    const geojson = {
      type: 'FeatureCollection',
      features: paddockFeatures,
    };

    // Remove existing layers if they exist
    if (map.current.getLayer('rotation-path')) map.current.removeLayer('rotation-path');
    if (map.current.getSource('rotation-path')) map.current.removeSource('rotation-path');
    if (map.current.getSource('paddocks')) {
      map.current.removeLayer('paddock-fills');
      map.current.removeLayer('paddock-borders');
      map.current.removeLayer('paddock-labels');
      map.current.removeSource('paddocks');
    }

    // Add source
    map.current.addSource('paddocks', {
      type: 'geojson',
      data: geojson,
    });

    const statusFill = [
      'match',
      ['get', 'status'],
      'grazing', STATUS_COLORS.grazing,
      'resting', STATUS_COLORS.resting,
      'ready', STATUS_COLORS.ready,
      STATUS_COLORS.resting, // default
    ];
    // Recency mode reads the pre-resolved per-feature color.
    const fillColor = colorMode === 'recency'
      ? ['coalesce', ['get', 'recency_color'], RECENCY_UNKNOWN]
      : statusFill;

    // Add fill layer
    map.current.addLayer({
      id: 'paddock-fills',
      type: 'fill',
      source: 'paddocks',
      paint: {
        'fill-color': fillColor,
        'fill-opacity': 0.45,
      },
    });

    // Add border layer
    map.current.addLayer({
      id: 'paddock-borders',
      type: 'line',
      source: 'paddocks',
      paint: {
        'line-color': fillColor,
        'line-width': 3,
      },
    });

    // Add labels
    map.current.addLayer({
      id: 'paddock-labels',
      type: 'symbol',
      source: 'paddocks',
      layout: {
        'text-field': ['get', 'code'],
        'text-size': 14,
        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 2,
      },
    });

    // Rotation path — the herd's recent route, drawn in recency mode when the
    // tracker gives us an ordered path. Connects paddock centroids.
    if (colorMode === 'recency' && tracker?.rotation_path?.length > 1) {
      const centroidById = {};
      paddockFeatures.forEach(f => {
        const ring = f.geometry.coordinates[0];
        const n = ring.length - 1; // last point closes the ring
        let sx = 0, sy = 0;
        for (let i = 0; i < n; i++) { sx += ring[i][0]; sy += ring[i][1]; }
        centroidById[f.properties.id] = [sx / n, sy / n];
      });
      const pathCoords = tracker.rotation_path
        .map(p => centroidById[p.paddock_id])
        .filter(Boolean);
      if (pathCoords.length > 1) {
        map.current.addSource('rotation-path', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: pathCoords },
          },
        });
        map.current.addLayer({
          id: 'rotation-path',
          type: 'line',
          source: 'rotation-path',
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
            'line-dasharray': [2, 1.5],
            'line-opacity': 0.9,
          },
        });
      }
    }

    // Click + hover handlers: layer-scoped and registered once per map
    // instance. They keep working across layer remove/re-add (Mapbox keys them
    // by layer id), so re-running this effect must NOT re-add them.
    if (!handlersBound.current) {
      map.current.on('click', 'paddock-fills', (e) => {
        setSelectedPaddock(e.features[0].properties);
      });
      map.current.on('mouseenter', 'paddock-fills', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'paddock-fills', () => {
        map.current.getCanvas().style.cursor = '';
      });
      handlersBound.current = true;
    }

    // Fit to paddocks only on the first build — not on tracker load or a
    // color-mode toggle, which would reset the user's pan/zoom.
    if (!didFit.current && paddockFeatures.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      paddockFeatures.forEach(feature => {
        feature.geometry.coordinates[0].forEach(coord => {
          bounds.extend(coord);
        });
      });
      map.current.fitBounds(bounds, { padding: 50 });
      didFit.current = true;
    }

  }, [mapLoaded, paddocks, colorMode, tracker]);

  // Summary stats
  const stats = {
    total: paddocks.length,
    grazing: paddocks.filter(p => p.status?.current_status === 'grazing').length,
    resting: paddocks.filter(p => p.status?.current_status === 'resting').length,
    ready: paddocks.filter(p => p.status?.is_ready_to_graze).length,
    totalArea: paddocks.reduce((sum, p) => sum + (parseFloat(p.area_ha) || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-900">Paddock Map</h1>
          <p className="text-earth-600">
            {stats.total} paddocks • {stats.totalArea.toFixed(1)} ha total
          </p>
        </div>
        <div className="flex items-center gap-2">
          {viewMode === 'map' && (
            <div className="flex items-center rounded-lg border border-earth-200 overflow-hidden mr-1">
              <button
                onClick={() => setColorMode('status')}
                className={`px-3 py-1.5 text-xs transition-colors ${
                  colorMode === 'status' ? 'bg-primary-100 text-primary-700' : 'hover:bg-earth-100'
                }`}
              >
                Status
              </button>
              <button
                onClick={() => setColorMode('recency')}
                className={`px-3 py-1.5 text-xs transition-colors ${
                  colorMode === 'recency' ? 'bg-primary-100 text-primary-700' : 'hover:bg-earth-100'
                }`}
              >
                Grazing recency
              </button>
            </div>
          )}
          <button
            onClick={() => setViewMode('map')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'map' ? 'bg-primary-100 text-primary-700' : 'hover:bg-earth-100'
            }`}
          >
            <Map className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'hover:bg-earth-100'
            }`}
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Legend — status or recency depending on color mode */}
      {colorMode === 'status' ? (
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.grazing }}></div>
            <span className="text-sm">Grazing ({stats.grazing})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.resting }}></div>
            <span className="text-sm">Resting ({stats.resting})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: STATUS_COLORS.ready }}></div>
            <span className="text-sm">Ready ({stats.ready})</span>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4 items-center">
          <span className="text-xs text-earth-500">Recently grazed</span>
          <div className="flex items-center gap-1">
            {[RECENCY_CLAY, RECENCY_MID, '#84cc16', RECENCY_GREEN].map((c) => (
              <div key={c} className="w-5 h-4 rounded-sm" style={{ backgroundColor: c }} />
            ))}
          </div>
          <span className="text-xs text-earth-500">Rested / recovered</span>
          <div className="flex items-center gap-2 ml-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: RECENCY_ATTENTION }}></div>
            <span className="text-sm">Needs a look</span>
          </div>
          <span className="text-xs text-earth-400">· · · rotation path</span>
        </div>
      )}

      {viewMode === 'map' ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Map */}
          <div className="lg:col-span-3">
            <div className="card overflow-hidden">
              {MAPBOX_TOKEN === 'YOUR_MAPBOX_TOKEN_HERE' ? (
                <div className="h-[500px] flex items-center justify-center bg-earth-100">
                  <div className="text-center p-6">
                    <Map className="h-12 w-12 text-earth-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-earth-700">Mapbox Token Required</h3>
                    <p className="text-earth-500 mt-2 max-w-md">
                      To enable the map, add your Mapbox access token to the .env file:
                    </p>
                    <code className="block mt-2 p-2 bg-earth-200 rounded text-sm">
                      VITE_MAPBOX_TOKEN=your_token_here
                    </code>
                    <p className="text-earth-500 mt-2 text-sm">
                      Get a free token at{' '}
                      <a 
                        href="https://account.mapbox.com/access-tokens/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:underline"
                      >
                        mapbox.com
                      </a>
                    </p>
                  </div>
                </div>
              ) : (
                <div 
                  ref={mapContainer} 
                  className="h-[500px] w-full"
                />
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Selected Paddock Info */}
            {selectedPaddock ? (
              <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-primary-900">{selectedPaddock.name}</h3>
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded-full text-white"
                    style={{ backgroundColor: STATUS_COLORS[selectedPaddock.status] }}
                  >
                    {STATUS_LABELS[selectedPaddock.status]}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-earth-600">Code</span>
                    <span className="font-medium">{selectedPaddock.code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-earth-600">Area</span>
                    <span className="font-medium">{selectedPaddock.area_ha} ha</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-earth-600">Cattle</span>
                    <span className="font-medium">{selectedPaddock.cattle_count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-earth-600">Days Rested</span>
                    <span className="font-medium">{selectedPaddock.days_rested || 0}</span>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button className="btn btn-primary flex-1 text-sm py-2">
                    {selectedPaddock.status === 'grazing' ? 'End Grazing' : 'Start Grazing'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="card p-4 text-center text-earth-500">
                <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Click a paddock on the map to view details</p>
              </div>
            )}

            {/* Quick Stats */}
            <div className="card p-4">
              <h3 className="font-semibold text-primary-900 mb-3">Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Beef className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-earth-600">Currently Grazing</div>
                    <div className="font-medium">
                      {paddocks
                        .filter(p => p.status?.current_status === 'grazing')
                        .reduce((sum, p) => sum + (p.status?.cattle_count || 0), 0)} cattle
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <div className="text-sm text-earth-600">Avg Rest Days</div>
                    <div className="font-medium">
                      {paddocks.length > 0 
                        ? Math.round(
                            paddocks.reduce((sum, p) => sum + (p.status?.days_rested || 0), 0) / 
                            paddocks.length
                          )
                        : 0} days
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Leaf className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm text-earth-600">Ready to Graze</div>
                    <div className="font-medium">{stats.ready} paddocks</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* List View */
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-earth-200">
            <thead className="bg-earth-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">
                  Paddock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">
                  Area
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">
                  Cattle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">
                  Days Rested
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-earth-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-earth-200">
              {paddocks.map((paddock) => (
                <tr key={paddock.id} className="hover:bg-earth-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-primary-900">{paddock.name}</div>
                    <div className="text-sm text-earth-500">{paddock.code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="px-2 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: STATUS_COLORS[paddock.status?.current_status] || STATUS_COLORS.resting }}
                    >
                      {STATUS_LABELS[paddock.status?.current_status] || 'Resting'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {paddock.area_ha} ha
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {paddock.status?.cattle_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {paddock.status?.days_rested || 0} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button className="text-primary-600 hover:text-primary-900">
                      {paddock.status?.current_status === 'grazing' ? 'End Grazing' : 'Start Grazing'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
